import { validRange } from '../../../utils/dateRanges';
import DateRangeControl from '../../common/DateRangeControl';
import React, { useState, useEffect } from "react";
import { Box, Card, CardContent, Typography, CircularProgress, Alert, Button, Stack, Paper } from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import { format, subDays, differenceInDays } from "date-fns";
import { apiService } from "../../../config/api";

interface DistributionData {
  name: string;
  value: number;
  color: string;
  [key: string]: string | number;
}

const StudyAdherenceDistribution: React.FC = () => {
  const [chartData, setChartData] = useState<DistributionData[]>([]);
  const [displayData, setDisplayData] = useState<DistributionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState<string>(
    format(subDays(new Date(), 30), "yyyy-MM-dd")
  );
  const [endDate, setEndDate] = useState<string>(
    format(new Date(), "yyyy-MM-dd")
  );
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [totalStudies, setTotalStudies] = useState(0);

  useEffect(() => {
    fetchDistributionData(startDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateDateRange = (start: string, end: string): boolean => {
    if (!start || !end) {
      setDateRangeError("Please select both start and end dates");
      return false;
    }

    const diff = differenceInDays(new Date(end), new Date(start));

    if (diff < 0) {
      setDateRangeError("End date must be after start date");
      return false;
    }

    setDateRangeError(null);
    return true;
  };

  const fetchDistributionData = async (start: string, end: string) => {
    if (!validateDateRange(start, end)) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiService.get(
        `/api/admin-adherence-monitor/study-distribution?startDate=${start}&endDate=${end}`
      );

      if (response.success) {
        const { distribution, totalStudies } = response;
        setTotalStudies(totalStudies);

        const data: DistributionData[] = [
          {
            name: `Excellent: >90%`,
            value: distribution.excellent || 0,
            color: "#4CAF50",
          },
          {
            name: `Good: 80-90%`,
            value: distribution.good || 0,
            color: "#00505C",
          },
          {
            name: `Fair: 70-80%`,
            value: distribution.fair || 0,
            color: "#7C3AED",
          },
          {
            name: `Poor: 40-70%`,
            value: distribution.poor || 0,
            color: "#FF9800",
          },
          {
            name: `Critical: <40%`,
            value: distribution.critical || 0,
            color: "#F44336",
          },
        ];

        setChartData(data);
        // Filter out 0 values for pie chart display
        setDisplayData(data.filter((d) => d.value > 0));
      } else {
        setError(response.error || "Failed to load distribution data");
      }
    } catch (err: any) {
      setError(err.message || "Error fetching distribution data");
    } finally {
      setLoading(false);
    }
  };

  const handleApplyDateRange = () => {
    fetchDistributionData(startDate, endDate);
  };

  return (
    <Card elevation={2} sx={{ mb: 3 }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Section Title */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
          Study Adherence Distribution
        </Typography>

        {/* Date Range Controls */}
        <Box sx={{ mb: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            gap={2}
            sx={{ alignItems: { xs: "stretch", sm: "flex-end" } }}
          >
            <DateRangeControl value={{ startDate: startDate || '', endDate: endDate || '' }}
              onChange={range => { setStartDate(range.startDate); setEndDate(range.endDate); }}
              startLabel="Start Date" endLabel="End Date" disabled={loading} />
            <Button
              variant="contained"
              size="small"
              onClick={handleApplyDateRange}
             disabled={loading || !validRange({ startDate, endDate })}>
              Apply
            </Button>
          </Stack>

          {/* Date Range Validation Error */}
          {dateRangeError && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {dateRangeError}
            </Alert>
          )}
        </Box>

        {/* Chart */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : chartData.length > 0 ? (
          <Box>
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <RechartsTooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload[0]) {
                      const data = payload[0].payload as DistributionData;
                      return (
                        <Paper
                          sx={{
                            p: 1.5,
                            bgcolor: "background.paper",
                            border: "1px solid",
                            borderColor: "divider",
                          }}
                          elevation={3}
                        >
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{ fontWeight: 600 }}
                          >
                            {data.name}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Studies: <strong>{data.value}</strong>
                          </Typography>
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            {((data.value / totalStudies) * 100).toFixed(1)}% of
                            total
                          </Typography>
                        </Paper>
                      );
                    }
                    return null;
                  }}
                />
                <Pie
                  data={displayData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => (name ? `${value}` : "")}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {displayData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>

            {/* Custom Legend showing all 5 adherence categories */}
            <Box
              sx={{
                mt: 3,
                display: "flex",
                flexWrap: "wrap",
                gap: 2.5,
                justifyContent: "center",
              }}
            >
              {chartData.map((item) => {
                const count =
                  displayData.find((d) => d.name === item.name)?.value || 0;
                return (
                  <Box
                    key={item.name}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minWidth: "150px",
                    }}
                  >
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        backgroundColor: item.color,
                        borderRadius: 1,
                        flexShrink: 0,
                      }}
                    />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {count} {count === 1 ? "study" : "studies"}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ) : (
          <Alert severity="info">
            No studies found for the selected date range.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default StudyAdherenceDistribution;
